/**
 * MoEngage configuration, read once from the CRA build-time env.
 *
 * Every key is optional. With nothing set the layer runs in MOCK mode:
 * events are logged to the console (debug builds only) and the SDK is never
 * downloaded. Going live is an env-only change — set REACT_APP_MOE_APP_ID and
 * REACT_APP_MOE_DATA_CENTER.
 *
 * Env is read from `process.env` once inside readConfig() (see the note there).
 */

/**
 * The business sheet sends mobile_number / email_id / dob as event properties.
 * Set to false (pending the compliance check) to strip them from event payloads;
 * they are still set as MoEngage user attributes.
 */
export const INCLUDE_PII_IN_EVENTS = true;

const VALID_DATA_CENTERS = ['dc_1', 'dc_2', 'dc_3', 'dc_4', 'dc_6'];

const str = (value) => (value == null ? '' : String(value).trim());

/** 'true' / 'false' → boolean; anything else → the fallback. */
const flag = (value, fallback) => {
    const v = str(value).toLowerCase();
    if (v === 'true') return true;
    if (v === 'false') return false;
    return fallback;
};

/**
 * @typedef {Object} MoeConfig
 * @property {boolean} enabled        Kill switch. false → every analytics call is a silent no-op.
 * @property {boolean} mockMode       true → console only, SDK never loaded.
 * @property {boolean} debug          Console-log (masked) every event, mock or live.
 * @property {boolean} piiInEvents    = INCLUDE_PII_IN_EVENTS (a code constant, not an env variable).
 * @property {boolean} persistContext true → remembered user context survives reloads (sessionStorage).
 * @property {string}  appId
 * @property {string}  dataCenter     dc_1 | dc_2 | dc_3 | dc_4 | dc_6
 * @property {'TEST'|'LIVE'} env
 * @property {number}  logLevel       SDK console verbosity (REACT_APP_MOE_LOG_LEVEL; default 1 TEST / 0 LIVE)
 * @property {string}  projectId
 * @property {string[]} configErrors  Reasons live mode was refused (shown once in debug).
 */

/** @returns {MoeConfig} */
export const readConfig = () => {
    // Read process.env ONCE. Webpack inlines the whole env object at every
    // reference to a key the build does not define, so seven direct
    // `process.env.REACT_APP_MOE_*` reads would inline it seven times.
    const ENV = process.env || {};
    const appId = str(ENV.REACT_APP_MOE_APP_ID);
    const dataCenter = str(ENV.REACT_APP_MOE_DATA_CENTER).toLowerCase();
    const envRaw = str(ENV.REACT_APP_MOE_ENV).toUpperCase();
    const env = envRaw === 'LIVE' ? 'LIVE' : 'TEST';
    // SDK console verbosity. Empty / invalid → 1 on TEST, 0 on LIVE.
    const logLevelRaw = str(ENV.REACT_APP_MOE_LOG_LEVEL);
    const logLevelNum = logLevelRaw === '' ? NaN : Number(logLevelRaw);
    const logLevel = Number.isInteger(logLevelNum) && logLevelNum >= 0 ? logLevelNum : (env === 'LIVE' ? 0 : 1);
    const configErrors = [];

    if (appId && !VALID_DATA_CENTERS.includes(dataCenter)) {
        // A wrong cluster silently sends data nowhere — refuse live mode instead.
        configErrors.push(
            `REACT_APP_MOE_DATA_CENTER must be one of ${VALID_DATA_CENTERS.join(', ')} (got "${dataCenter}")`
        );
    }

    return Object.freeze({
        enabled: flag(ENV.REACT_APP_MOE_ENABLED, true),
        mockMode: !appId || configErrors.length > 0,
        // Default: chatty in local dev, silent in production builds.
        debug: flag(ENV.REACT_APP_MOE_DEBUG, ENV.NODE_ENV !== 'production'),
        piiInEvents: INCLUDE_PII_IN_EVENTS,
        persistContext: flag(ENV.REACT_APP_MOE_PERSIST_CONTEXT, false),
        appId,
        dataCenter,
        env,
        logLevel,
        projectId: str(ENV.REACT_APP_MOE_PROJECT_ID),
        configErrors: Object.freeze(configErrors),
    });
};

export const config = readConfig();
